import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ContentPageThemeServices } from "@/services/bedrock/ContentPageThemeServices";
import { SegmentationDetailServices } from "@/services/bedrock/SegmentationDetailServices";
import { ContentPageTheme, ContentPageThemeDefault } from "@/services/bedrock/dtos/ContentPageTheme";
import { usePageThemes } from "@/utils/usePageThemes";

const THEMES_PER_PAGE = 50;
const STALE_TIME = 5 * 60_000;

export interface EditorThemeOption {
  theme: ContentPageTheme;
  label: string;
  kind: "default" | "segmentDefault" | "other";
}

async function fetchAllDefaults(): Promise<ContentPageThemeDefault[]> {
  const first = await ContentPageThemeServices.getPageThemeDefaults(1, THEMES_PER_PAGE);
  const totalPages = first.pagination.totalPages ?? 1;
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) =>
      ContentPageThemeServices.getPageThemeDefaults(i + 2, THEMES_PER_PAGE)
    )
  );
  return [first, ...rest].flatMap(page => page.content);
}

/**
 * The coach's themes in switcher order: the coach-wide default, then segmented defaults, then
 * everything else alphabetically. Each theme appears once.
 */
export function useEditorThemes() {
  const { themes: firstPage, isLoading, rawResponse } = usePageThemes({ page: 1, perPage: THEMES_PER_PAGE });
  const totalPages = rawResponse?.pagination.totalPages ?? 1;

  const rest = useQuery({
    queryKey: ["contentPageThemesList", "rest", THEMES_PER_PAGE, totalPages],
    queryFn: async () => {
      const pages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          ContentPageThemeServices.getPageThemes({ page: i + 2, perPage: THEMES_PER_PAGE })
        )
      );
      return pages.flatMap(page => page.content);
    },
    enabled: totalPages > 1,
    staleTime: STALE_TIME,
  });

  const defaults = useQuery({
    queryKey: ["contentPageThemeDefaults", "all"],
    queryFn: fetchAllDefaults,
    staleTime: STALE_TIME,
  });

  const segments = useQuery({
    queryKey: ["segmentationDetails", "all"],
    queryFn: () => SegmentationDetailServices.getSegmentationDetails(["STATE", "REGION", "BRANCH"], undefined, 1, 100),
    staleTime: STALE_TIME,
  });

  const options = useMemo<EditorThemeOption[]>(() => {
    const all = [...firstPage, ...(rest.data ?? [])];
    const byId = new Map(all.map(theme => [theme.id, theme]));
    const segmentNames = new Map((segments.data?.content ?? []).map(s => [String(s.id), s.name]));
    const used = new Set<number>();
    const result: EditorThemeOption[] = [];

    const add = (theme: ContentPageTheme | undefined, kind: EditorThemeOption["kind"], label: string) => {
      if (!theme || used.has(theme.id)) return;
      used.add(theme.id);
      result.push({ theme, kind, label });
    };

    const rows = (defaults.data ?? []).filter(row => row.playerId == null);
    rows
      .filter(row => row.coachId != null && !row.segmentationDetailId)
      .forEach(row => {
        const theme = byId.get(row.themeId);
        if (theme) add(theme, "default", `${theme.name} · Default`);
      });
    rows
      .filter(row => !!row.segmentationDetailId)
      .forEach(row => {
        const theme = byId.get(row.themeId);
        const segment = segmentNames.get(String(row.segmentationDetailId)) || "segment";
        if (theme) add(theme, "segmentDefault", `${theme.name} · Default (${segment})`);
      });
    [...all].sort((a, b) => a.name.localeCompare(b.name)).forEach(theme => add(theme, "other", theme.name));

    return result;
  }, [firstPage, rest.data, defaults.data, segments.data]);

  return { options, isLoading: isLoading || defaults.isLoading || (totalPages > 1 && rest.isLoading) };
}
